import { Countries } from './countriesList';
import { CurrencyType } from '../types';

export const BASE_CURRENCY: CurrencyType = 'USD';

export enum COMPENSATION_TYPE {
  HOURLY = 'hourly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export const DEFAULT_MAX_COMPENSATION = {
  YEARLY: 1200000,
  MONTHLY: 100000,
  HOURLY: 10000,
};

export const COUNTRY_SPECIFIC_MAX_COMPENSATION: Partial<
  Record<Countries, { YEARLY: number; MONTHLY: number; HOURLY: number }>
> = {
  [Countries.India]: {
    YEARLY: 10000000,
    MONTHLY: 1000000,
    HOURLY: 10000,
  },
};

export const COUNTRIES_WITH_YEARLY_COMPENSATION = [
  Countries.India,
  Countries.UnitedStates,
];

export const COUNTRIES_WITH_CUSTOM_CURRENCY_INPUT: Partial<
  Record<Countries, { symbol: string; currency: CurrencyType }>
> = {
  [Countries.India]: {
    symbol: '₹',
    currency: 'INR',
  },
  [Countries.UnitedKingdom]: {
    symbol: '£',
    currency: 'GBP',
  },
};

export const COMPENSATION_OPTIONS = [
  { type: COMPENSATION_TYPE.HOURLY, label: 'Hourly' },
  { type: COMPENSATION_TYPE.MONTHLY, label: 'Monthly' },
  { type: COMPENSATION_TYPE.YEARLY, label: 'Yearly' },
];

export interface ICompensationInfoBenefit {
  title: string;
  description?: string;
}

export interface ICompensationInfoContent {
  paymentDetails: string[];
  benefits: ICompensationInfoBenefit[];
}

const COMPETITIVE_COMPENSATION_BENEFIT: ICompensationInfoBenefit = {
  title: 'Competitive compensation',
  description:
    "We're ready to pay premium compensation for your ever-growing skills and impressive results.",
};

const latamContent: ICompensationInfoContent = {
  paymentDetails: [
    'Compensation in USD',
    'Monthly payment based on hourly rate',
    'Gross compensation, before taxes',
  ],
  benefits: [
    COMPETITIVE_COMPENSATION_BENEFIT,
    {
      title: 'Accounting consultancy',
      description:
        "No need to handle tax matters on your own! You'll get comprehensive support that covers all accounting inquiries.",
    },
  ],
};

const bulgariaContent: ICompensationInfoContent = {
  paymentDetails: [
    'Compensation in USD',
    'Monthly payment based on hourly rate',
    'Gross compensation, before taxes',
    'B2B Engagement',
  ],
  benefits: [
    COMPETITIVE_COMPENSATION_BENEFIT,
    {
      title: 'Accounting consultancy',
      description:
        "No need to handle tax matters on your own! You'll get comprehensive support that covers all payroll and accounting inquiries concerning B2B engagement.",
    },
  ],
};

const europeB2bContent: ICompensationInfoContent = {
  paymentDetails: [
    'Compensation in USD',
    'Monthly payment based on hourly rate',
    'Gross compensation, before taxes',
    'B2B Engagement',
  ],
  benefits: [
    COMPETITIVE_COMPENSATION_BENEFIT,
    {
      title: 'Accounting consultancy',
      description:
        "No need to handle tax matters on your own! You'll get comprehensive support that covers all accounting inquiries concerning B2B engagement.",
    },
  ],
};

const europeNoAccountingContent: ICompensationInfoContent = {
  paymentDetails: [
    'Compensation in USD',
    'Monthly payment based on hourly rate',
    'Gross compensation, before taxes',
    'B2B Engagement',
  ],
  benefits: [COMPETITIVE_COMPENSATION_BENEFIT],
};

const ukraineContent: ICompensationInfoContent = {
  paymentDetails: [
    'Compensation in USD',
    'Monthly payment',
    'Gross compensation, before taxes',
    'B2B Engagement',
  ],
  benefits: [
    COMPETITIVE_COMPENSATION_BENEFIT,
    {
      title: 'Accounting consultancy',
      description:
        "No need to handle tax matters on your own! You'll get comprehensive support that covers all accounting inquiries concerning PE.",
    },
  ],
};

const usaContent: ICompensationInfoContent = {
  paymentDetails: ['Gross compensation, before taxes'],
  benefits: [COMPETITIVE_COMPENSATION_BENEFIT],
};

const indiaContent: ICompensationInfoContent = {
  paymentDetails: [
    'Payments in local currency, monthly',
    'Gross compensation, before taxes',
  ],
  benefits: [
    COMPETITIVE_COMPENSATION_BENEFIT,
    {
      title: 'Accounting consultancy',
      description:
        "No need to handle tax matters on your own! You'll get comprehensive support that covers all payroll and accounting inquiries.",
    },
  ],
};

export const COUNTRY_COMPENSATION_INFO: Partial<
  Record<Countries, ICompensationInfoContent>
> = {
  [Countries.Argentina]: latamContent,
  [Countries.Brazil]: latamContent,
  [Countries.Colombia]: latamContent,
  [Countries.Guatemala]: latamContent,
  [Countries.Mexico]: latamContent,
  [Countries.Bulgaria]: bulgariaContent,
  [Countries.Poland]: europeB2bContent,
  [Countries.Portugal]: europeB2bContent,
  [Countries.Spain]: europeB2bContent,
  [Countries.Romania]: europeNoAccountingContent,
  [Countries.Slovenia]: europeNoAccountingContent,
  [Countries.Slovakia]: europeNoAccountingContent,
  [Countries.Ukraine]: ukraineContent,
  [Countries.UnitedStates]: usaContent,
  [Countries.India]: indiaContent,
};

export enum CompensationBenefitType {
  Employment = 'Employment',
  Referral = 'Referral',
  Professional = 'Professional',
}

export const CompensationBenefitsOrder = [
  CompensationBenefitType.Employment,
  CompensationBenefitType.Referral,
  CompensationBenefitType.Professional,
];
